<template>
  <base-modal
    name="basic-modal"
    :visible="show"
    close-button
    close-outside
    classes="!w-[560px]"
    @close="closeModal"
  >
    <div class="text-7 font-bold text-color-1 mb-7">
      {{ $t('DetailMassPayments | modal | recipient | title') }}
    </div>

    <div class="flex flex-col gap-y-9 mb-10">
      <input-with-autocomplete
        v-model="formData.email"
        :items="recipients"
        :label="$t('DetailMassPayments | modal | input | email')"
        :placeholder="
          $t('DetailMassPayments | modal | input | placeholderEmail')
        "
        :danger="errorEmail"
        :error-message="
          $t('DetailMassPayments | modal | input | errorEmailPlaceholder')
        "
        :min-chars="3"
        :debounce-ms="500"
        :maxlength="64"
        item-key="id"
        item-label="email"
        item-sublabel="note"
        item-value="email"
        @search="loadRecipients"
        @select="onRecipientSelect"
      />

      <div class="flex flex-col gap-y-2">
        <div class="relative">
          <base-input
            v-model="formData.amount"
            :label="$t('DetailMassPayments | modal | input | amount')"
            :additional-classes="['!pr-14']"
            :placeholder="
              $t('DetailMassPayments | modal | input | placeholderAmount')
            "
            :danger="errorAmount"
          />
          <div
            class="absolute top-4.5 right-4 text-color-3 font-medium text-sm"
          >
            USDT
          </div>
        </div>
        <div v-if="errorAmount" class="text-error text-sm">
          {{
            $t('DetailMassPayments | modal | input | errorAmountPlaceholder')
          }}
        </div>
      </div>

      <div class="flex flex-col gap-y-2">
        <base-input
          v-model="formData.description"
          :label="$t('DetailMassPayments | modal | input | comment')"
          :maxlength="120"
          :placeholder="
            $t('DetailMassPayments | modal | input | commentPlaceholder')
          "
          :danger="errorDescription"
        />
        <div v-if="errorDescription" class="text-error text-sm">
          {{
            $t(
              'DetailMassPayments | modal | input | errorDescriptionPlaceholder'
            )
          }}
        </div>
      </div>
    </div>

    <div class="w-full">
      <base-button
        class="!w-full"
        type="cta"
        size="md"
        :disabled="disableButton"
        @onClick="submitForm"
      >
        <base-loader v-if="actionLoading" :stroke-width="3" />
        <div v-else>
          {{ $t('DetailMassPayments | modal | button | invite') }}
        </div>
      </base-button>
    </div>
  </base-modal>
</template>
<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

import BaseModal from '@emcd-shared/base-modal/BaseModal.vue'
import BaseInput from '@emcd-shared/base-input'
import InputWithAutocomplete from '@/shared/ui/InputWithAutocomplete/InputWithAutocomplete.vue'
import BaseLoader from '@/packages/BaseLoader/Spinner/Spinner.vue'
import BaseButton from '@/shared/ui/BaseButton/BaseButton.vue'

import {
  DETAIL_MASS_PAYMENT_DISPATCHERS,
  DETAIL_MASS_PAYMENT_GETTERS,
} from '@/shared/constants/store-constants/detailMassPayment'
import { massPaymentsService } from '@/shared/services/mass-payments.service'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emits = defineEmits(['update:modelValue', 'submitForm', 'onClose'])

const store = useStore()
const { t } = useI18n()

const show = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emits('update:modelValue', value)
  },
})

const actionLoading = computed(
  () => store.getters[DETAIL_MASS_PAYMENT_GETTERS.actionLoading]
)

const disableButton = computed(
  () =>
    !formData.email ||
    !formData.amount ||
    actionLoading.value ||
    errorEmail.value ||
    errorAmount.value ||
    errorDescription.value
)

const recipients = ref([])

const formData = reactive({
  email: null,
  description: null,
  amount: null,
})

const errorEmail = computed(() =>
  formData.email?.trim()
    ? !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    : false
)

const errorAmount = computed(() => {
  if (!formData.amount) return false
  const amount = parseFloat(formData.amount)
  return isNaN(amount) || amount <= 0
})

const errorDescription = computed(() => {
  if (!formData.description) return false
  return formData.description.length > 120
})

function closeModal() {
  show.value = false

  emits('onClose')
}

async function loadRecipients(search) {
  try {
    const res = await massPaymentsService.getRecipients({
      search: search || '',
      page: 1,
      limit: 999,
      sort_by: 'email',
      sort_direction: 'asc',
    })
    recipients.value = res.data?.recipients ?? []
  } catch {
    recipients.value = []
  }
}

function onRecipientSelect(recipient) {
  formData.email = recipient?.email ?? formData.email
}

function resetFormData() {
  Object.keys(formData).forEach((key) => {
    formData[key] = null
  })
}

async function submitForm() {
  if (actionLoading.value) return

  await store.dispatch(DETAIL_MASS_PAYMENT_DISPATCHERS.goToAddUser, formData)

  resetFormData()
  emits('submitForm')
}

watch(show, () => {
  if (!show.value) {
    resetFormData()
  }
})
</script>
